import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { api } from '../api.js'

export default function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''

  const [validating, setValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [tokenEmail, setTokenEmail] = useState('')
  const [tokenError, setTokenError] = useState('')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [done, setDone] = useState(false)

  useEffect(() => {
    if (!token) {
      setTokenError('Ingen återställningslänk hittades.')
      setValidating(false)
      return
    }
    api.auth.validateResetToken(token)
      .then((data) => {
        setTokenValid(true)
        setTokenEmail(data.email)
      })
      .catch((err) => {
        setTokenError(err.message || 'Ogiltig eller utgången länk.')
      })
      .finally(() => setValidating(false))
  }, [token])

  async function handleSubmit(e) {
    e.preventDefault()
    if (password !== confirm) { setError('Lösenorden matchar inte'); return }
    if (password.length < 8) { setError('Lösenordet måste vara minst 8 tecken'); return }
    setLoading(true)
    setError('')
    try {
      await api.auth.resetPassword(token, password)
      setDone(true)
      setTimeout(() => navigate('/login'), 3000)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--app-bg)] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <span className="text-5xl">🏆</span>
          <h1 className="mt-3 text-2xl font-bold text-gray-900 dark:text-slate-100">Grannäventyret</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">Välj ett nytt lösenord</p>
        </div>

        <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          {validating && (
            <p className="py-6 text-center text-sm text-gray-400 dark:text-slate-500">Verifierar länk...</p>
          )}

          {!validating && tokenError && (
            <div className="text-center">
              <div className="mb-3 text-4xl">⚠️</div>
              <p className="mb-4 text-sm text-red-600 dark:text-red-300">{tokenError}</p>
              <Link to="/forgot-password" className="text-sm font-medium text-indigo-600 hover:underline dark:text-sky-400">
                Begär en ny länk →
              </Link>
            </div>
          )}

          {!validating && tokenValid && !done && (
            <form onSubmit={handleSubmit} className="space-y-4">
              {tokenEmail && (
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  Återställer lösenord för <strong className="text-gray-700 dark:text-slate-200">{tokenEmail}</strong>
                </p>
              )}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-slate-300">
                  Nytt lösenord
                </label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minst 8 tecken"
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-slate-300">
                  Bekräfta lösenord
                </label>
                <input
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Upprepa lösenordet"
                  autoComplete="new-password"
                  className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                />
              </div>
              {error && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-400/20 dark:bg-red-500/10 dark:text-red-300">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl bg-indigo-600 py-2.5 font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 dark:bg-sky-500 dark:text-slate-950"
              >
                {loading ? 'Sparar...' : 'Spara nytt lösenord'}
              </button>
            </form>
          )}

          {done && (
            <div className="text-center">
              <div className="mb-3 text-4xl">✅</div>
              <h2 className="mb-2 font-semibold text-gray-800 dark:text-slate-100">Lösenord uppdaterat!</h2>
              <p className="text-sm text-gray-500 dark:text-slate-400">Du skickas till inloggningen om ett ögonblick...</p>
            </div>
          )}
        </div>

        <p className="mt-4 text-center text-sm text-gray-500 dark:text-slate-400">
          <Link to="/login" className="font-medium text-indigo-600 hover:underline dark:text-sky-400">
            ← Tillbaka till inloggning
          </Link>
        </p>
      </div>
    </div>
  )
}
